import axios from 'axios';
import mime from 'mime';
import { useCallback } from 'react';

import { isDrawable } from '@/services/utils';

import { useUrlValidator } from './useURLValidator';

/**
 * True when the URL has a path segment that could be a file/asset (including
 * extension-less CDN paths). Bare hostnames / homepages are skipped so we do
 * not probe getUrlInfo for values that can never be previewed.
 */
const looksLikeFileUrl = (url: string): boolean => {
    try {
        const pathname = new URL(url).pathname.replace(/\/+$/, '');
        return pathname.length > 0;
    } catch {
        return false;
    }
};

/**
 * Path without query/hash so mime lookup works for CDN URLs like
 * `.../image.png?w=250&h=150` (mime.getType returns null if `?` is present).
 */
const urlPathForMimeLookup = (url: string): string => {
    try {
        return new URL(url).pathname;
    } catch {
        return url.split(/[?#]/)[0];
    }
};

/**
 * A hook that returns a boolean indicating if a given URL could be rendered by the browser in addition
 * to the mimeType behind the URL.
 */
const usePreviewValidator = () => {

    const { validateUrlAsync } = useUrlValidator();

    const validatePreviewAsync = useCallback(async (url: string): Promise<{
        drawable: boolean;
        mimeType: string;
    }> => {

        const trimmedUrl = url.trim();
        if (!trimmedUrl) {
            return { drawable: false, mimeType: '' };
        }

        // First try to figure out the content type from the URL itself by checking the file extension (so
        // we can bypass cross-origin restrictions), otherwise we check the `getUrlInfo` service that would check the
        // actual mime type for us. Strip query/hash before mime lookup — otherwise `...?w=250` returns null.
        const extensionMimeType = mime.getType(urlPathForMimeLookup(trimmedUrl));
        let mimeType = '';
        let drawable = false;

        if (extensionMimeType) {
            mimeType = extensionMimeType;
            // We need now to check if the mimeType is supported by the browser or not.
            if (isDrawable(extensionMimeType as string)) {
                drawable = true;
            }
        } else if (!looksLikeFileUrl(trimmedUrl)) {
            // Homepage / bare domain — not a previewable asset; skip the server round-trip
            return { drawable: false, mimeType: '' };
        } else {
            try {
                mimeType = await validateUrlAsync(trimmedUrl) || '';
                if (mimeType && isDrawable(mimeType)) {
                    drawable = true;
                }
            } catch (error) {
                // Let callers ignore aborted checks when the URL changes mid-flight
                if (axios.isCancel(error)) {
                    throw error;
                }
                return { drawable: false, mimeType: '' };
            }
        }

        return { drawable, mimeType };

        // We only need one instance of this function.
        // eslint-disable-next-line
    }, []);

    return { validatePreviewAsync };

};

export { usePreviewValidator };
